import { useMemo } from 'react';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { cleanEmpty } from 'utils/cleanEmpty';

import { MerchantsListFilterChips } from './MerchantsListFilterChips';
import { MerchantsListFilterForm } from './MerchantsListFilterForm';
import { merchantsFilterMapper } from './utils/merchantsFilterMapper';

export type MerchantsFilterValues = {
  archive?: boolean | null;
  name: string;
  companies: string[];
  partner: boolean | null;
  platform: string;
  referrals: string[];
};

export const MERCHANTS_FILTER_DEFAULT_VALUES = {
  archive: false,
  name: '',
  companies: [],
  partner: null,
  platform: '',
  referrals: [],
};

export const MerchantsListFilter = () => {
  const translate = useTypedTranslate();

  const { filterValues } = useListContext();
  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<MerchantsFilterValues>({
      defaultValues: merchantsFilterMapper.fromUrl(filterValues),
      resetValues: MERCHANTS_FILTER_DEFAULT_VALUES,
    });

  useSyncFilter({ appliedFilters });

  const listFilters = useMemo(() => {
    return cleanEmpty(appliedFilters, false);
  }, [appliedFilters]);

  const isListFilterExist =
    Object.keys(listFilters).length > 0 && form.formState.isDirty && form.formState.isSubmitted;

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton key="select-column" />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <MerchantsListFilterForm />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label={translate('app.buttons.reset')} onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters} isSkipBooleanValues={false}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <MerchantsListFilterChips />
            <FilterClearButton
              key="clear"
              onClick={() => {
                onReset();
              }}
              visible={isListFilterExist}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
    </>
  );
};
